import { Dispatch } from "redux";
import SessionInteractor from "../../../domain/interactors/SessionInteractor";
import SignatoryInteractor from "../../../domain/interactors/SignatoryInteractor";
import { RootState } from "../reducers";

export enum ActionTypes {
  LOGIN_PROCESSING = "@session/LOGIN_PROCESSING",
  LOGIN_COMPLETE = "@session/LOGIN_COMPLETE",
  LOGIN_ERROR = "@session/LOGIN_ERROR",
  COGNITO_AUTH_LOGIN_PROCESSING = "@session/COGNITO_AUTH_LOGIN_PROCESSING",
  COGNITO_AUTH_LOGIN_COMPLETE = "@session/COGNITO_AUTH_LOGIN_COMPLETE",
  COGNITO_AUTH_LOGIN_ERROR = "@session/COGNITO_AUTH_LOGIN_ERROR",
  EXTERNAL_LOGIN_PROCESSING = "@session/EXTERNAL_LOGIN_PROCESSING",
  EXTERNAL_LOGIN_COMPLETE = "@session/EXTERNAL_LOGIN_COMPLETE",
  EXTERNAL_LOGIN_ERROR = "@session/EXTERNAL_LOGIN_ERROR",
  MENU_VISIBLE = "@session/MENU_VISIBLE",
  MENU_SELECTED = "@session/MENU_SELECTED",
  REQUEST_PASSWORD_UPDATE_PROCESSING = "@session/REQUEST_PASSWORD_UPDATE_PROCESSING",
  REQUEST_PASSWORD_UPDATE_COMPLETE = "@session/REQUEST_PASSWORD_UPDATE_COMPLETE",
  REQUEST_PASSWORD_UPDATE_ERROR = "@session/REQUEST_PASSWORD_UPDATE_ERROR",
  REQUEST_PASSWORD_RESTORE = "@session/REQUEST_PASSWORD_RESTORE",
  PASSWORD_UPDATE_PROCESSING = "@session/PASSWORD_UPDATE_PROCESSING",
  PASSWORD_UPDATE_COMPLETE = "@session/PASSWORD_UPDATE_COMPLETE",
  PASSWORD_UPDATE_ERROR = "@session/PASSWORD_UPDATE_ERROR",
  CURRENT_PDF_INSTANCE = "@session/CURRENT_PDF_INSTANCE",
  SIGNATORY_LOAD_BY_EMAIL_PROCESSING = "@signatory/SIGNATORY_LOAD_BY_EMAIL_PROCESSING",
  SIGNATORY_LOAD_BY_EMAIL_COMPLETE = "@signatory/SIGNATORY_LOAD_BY_EMAIL_COMPLETE",
  SIGNATORY_LOAD_BY_EMAIL_ERROR = "@signatory/SIGNATORY_LOAD_BY_EMAIL_ERROR",
  SIGNATORY_AUTH_LOAD_BY_EMAIL_PROCESSING = "@signatory/SIGNATORY_AUTH_LOAD_BY_EMAIL_PROCESSING",
  SIGNATORY_AUTH_LOAD_BY_EMAIL_COMPLETE = "@signatory/SIGNATORY_AUTH_LOAD_BY_EMAIL_COMPLETE",
  SIGNATORY_AUTH_LOAD_BY_EMAIL_ERROR = "@signatory/SIGNATORY_AUTH_LOAD_BY_EMAIL_ERROR",
  SIGNATORY_SEND_PIN_PROCESSING = "@session/SIGNATORY_SEND_PIN_PROCESSING",
  SIGNATORY_SEND_PIN_COMPLETE = "@session/SIGNATORY_SEND_PIN_COMPLETE",
  SIGNATORY_SEND_PIN_ERROR = "@session/SIGNATORY_SEND_PIN_ERROR",
  CLEAR_LOGIN_ERROR = "@session/CLEAR_LOGIN_ERROR",
  SET_LOGIN_ERROR = "@session/SET_LOGIN_ERROR",
  MFA_RESPOND_PROCESSING = "@session/MFA_RESPOND_PROCESSING",
  MFA_RESPOND_COMPLETE = "@session/MFA_RESPOND_COMPLETE",
  MFA_RESPOND_ERROR = "@session/MFA_RESPOND_ERROR",
}

export const menuSelect = (menuItem: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: ActionTypes.MENU_SELECTED, payload: menuItem });
  };
};

export const menuVisible = (visibility: true) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: ActionTypes.MENU_VISIBLE, payload: visibility });
  };
};

export const login = (username: string, password: string) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    dispatch({ type: ActionTypes.LOGIN_PROCESSING });
    try {
      dispatch({
        type: ActionTypes.LOGIN_COMPLETE,
        payload: await deps.sessionInteractor.login(username, password),
      });
    } catch (error) {
      dispatch({ type: ActionTypes.LOGIN_ERROR, payload: error });
    }
  };
};

export const loginExternal = (shortId: string, password: string) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { signatoryInteractor: SignatoryInteractor }
  ) => {
    dispatch({ type: ActionTypes.SIGNATORY_AUTH_LOAD_BY_EMAIL_PROCESSING });
    try {
      dispatch({
        type: ActionTypes.SIGNATORY_AUTH_LOAD_BY_EMAIL_COMPLETE,
        payload: await deps.signatoryInteractor.getSignatoryByShortIdThenVerify(
          shortId,
          password
        ),
      });
    } catch (error) {
      dispatch({
        type: ActionTypes.SIGNATORY_AUTH_LOAD_BY_EMAIL_ERROR,
        payload: error,
      });
    }
  };
};

export const sendNewPin = (shortId: string, forceResend?: boolean, email?: string) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { signatoryInteractor: SignatoryInteractor }
  ) => {
    dispatch({ type: ActionTypes.SIGNATORY_SEND_PIN_PROCESSING });
    try {
      dispatch({
        type: ActionTypes.SIGNATORY_SEND_PIN_COMPLETE,
        payload: await deps.signatoryInteractor.sendNewPin(shortId, forceResend, email),
      });
    } catch (error) {
      dispatch({ type: ActionTypes.SIGNATORY_SEND_PIN_ERROR, payload: error });
    }
  };
};

export const clearLoginError = () => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: ActionTypes.CLEAR_LOGIN_ERROR });
  };
};

export const setLoginError = (messageError: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: ActionTypes.SET_LOGIN_ERROR, payload: messageError });
  };
};

export const sessionCognitoAuthLogin = (username: string, password: string) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    dispatch({ type: ActionTypes.COGNITO_AUTH_LOGIN_PROCESSING });
    try {
      dispatch({
        type: ActionTypes.COGNITO_AUTH_LOGIN_COMPLETE,
        payload: await deps.sessionInteractor.cognitoAuthLogin(
          username,
          password
        ),
      });
    } catch (error) {
      dispatch({ type: ActionTypes.COGNITO_AUTH_LOGIN_ERROR, payload: error });
    }
  };
};

export const updateFinalPassword = (username: string, tempPassword: string, newPassword: string) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    dispatch({ type: ActionTypes.COGNITO_AUTH_LOGIN_PROCESSING });
    try {
      dispatch({
        type: ActionTypes.COGNITO_AUTH_LOGIN_COMPLETE,
        payload: await deps.sessionInteractor.cognitoNewPasswordLogin(
          username,
          tempPassword,
          newPassword
        ),
      });
    } catch (error) {
      dispatch({ type: ActionTypes.COGNITO_AUTH_LOGIN_ERROR, payload: error });
    }
  };
};

export const sessionCognitoLogin = (tokenData: string) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    dispatch({ type: ActionTypes.LOGIN_PROCESSING });
    try {
      dispatch({ type: ActionTypes.LOGIN_COMPLETE, payload: tokenData });
    } catch (error) {
      dispatch({ type: ActionTypes.LOGIN_ERROR, payload: error });
    }
  };
};

export const requestPasswordUpdate = (email: string) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    dispatch({ type: ActionTypes.REQUEST_PASSWORD_UPDATE_PROCESSING });
    try {
      dispatch({
        type: ActionTypes.REQUEST_PASSWORD_UPDATE_COMPLETE,
        payload: await deps.sessionInteractor.requestPasswordUpdate(email),
      });
    } catch (error) {
      dispatch({
        type: ActionTypes.REQUEST_PASSWORD_UPDATE_ERROR,
        payload: error,
      });
    }
  };
};

export const cleanRequestPassword = () => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: ActionTypes.REQUEST_PASSWORD_RESTORE });
  };
};

export const passwordUpdate = (
  email: string,
  password: string,
  resetCode: string
) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    dispatch({ type: ActionTypes.PASSWORD_UPDATE_PROCESSING });
    try {
      dispatch({
        type: ActionTypes.PASSWORD_UPDATE_COMPLETE,
        payload: await deps.sessionInteractor.passwordUpdate(
          email,
          password,
          resetCode
        ),
      });
    } catch (error) {
      dispatch({ type: ActionTypes.PASSWORD_UPDATE_ERROR, payload: error });
    }
  };
};

export const setCurrentPDFInstance = (pdfInstance: any) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    dispatch({ type: ActionTypes.CURRENT_PDF_INSTANCE, payload: pdfInstance });
  };
};

export const changePassword = (currentPassword: string, newPassword: string) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    return await deps.sessionInteractor.changePassword(currentPassword, newPassword);
  };
};

export const mfaRespond = (username: string, session: string, code: string) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    dispatch({ type: ActionTypes.MFA_RESPOND_PROCESSING });
    try {
      dispatch({
        type: ActionTypes.MFA_RESPOND_COMPLETE,
        payload: await deps.sessionInteractor.mfaRespond(username, session, code),
      });
    } catch (error) {
      dispatch({ type: ActionTypes.MFA_RESPOND_ERROR, payload: error });
    }
  };
};

export const cognitoDecode = (tokenUrlencoded: URLSearchParams) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    return await deps.sessionInteractor.cognitoDecode(tokenUrlencoded);
  };
};

export const cognitoVerify = (codeUrlencoded: URLSearchParams) => {
  return async (
    dispatch: Dispatch,
    getState: () => RootState,
    deps: { sessionInteractor: SessionInteractor }
  ) => {
    return await deps.sessionInteractor.cognitoVerify(codeUrlencoded);
  };
};

